'use client'

import { Carousel, CarouselContent, CarouselItem } from '@lastbrand/web-ui/carousel'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useCallback } from 'react'

import { ProductReviewCardDs } from '@src/components/blocks/product-review-card-ds'
import { RichTextDS } from '@src/components/blocks/rich-text-ds'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import type GenericContent from '@src/quince-site-common/src/domains/GenericContent'
import RichTextDomain from '@src/quince-site-common/src/domains/RichText'
import { useBatchedAnalyticsForProductCard } from '@src/quince-site-common/src/hooks/useBatchedAnalyticsForProductCard'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import { SectionProps } from '@src/SectionRenderer/SectionRenderer.types'
import {
  getContentContainerTrackingMetadata,
  getTrackingContext,
} from '@src/SectionRenderer/sections/productListSection/productListSection.helpers'
import { getFindingMethodAggregate } from '@src/telemetry/findingMethod'

import type { Document, Node } from '@contentful/rich-text-types'

export const COLUMNS = 6

// Flatten a Contentful rich-text document to its plain text, so the header's
// test id can be keyed off the actual heading copy (mirrors faq-ds / back-to-school).
const plainTextFromNode = (node: Node): string => {
  if (node.nodeType === 'text' && 'value' in node) return (node as { value: string }).value
  if ('content' in node && Array.isArray((node as { content: Node[] }).content)) {
    return (node as { content: Node[] }).content.map(plainTextFromNode).join(' ')
  }
  return ''
}

export const plainTextFromDocument = (doc: Document | undefined | null): string =>
  doc && Array.isArray(doc.content)
    ? doc.content
        .map(n => plainTextFromNode(n as Node))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim()
    : ''

// Outer band: full-viewport width — colored band / padding lives here so a
// 100% wrapper paints edge to edge on wide monitors. No max-width.
export const containerClass = css({
  width: '100%',
  py: { base: 'space-48', lg: 'space-64' },
  pr: { base: 0, lg: 'space-48' },
  pl: { base: 'space-16', lg: 'space-48' },
})

// Inner content: capped at 1504px and centered. Heading + grid/carousel live
// here. Matches the canonical "outer 100% / inner 1504" widget pattern (see
// `faq-ds.tsx`, `footer-widget-ds.tsx` `maxWidthContainerStyles`,
// `back-to-school-ds-widget.tsx` `sectionInnerClass`).
export const innerClass = css({
  display: 'flex',
  flexDirection: 'column',
  gap: { base: 'space-16', lg: 'space-24' },
  maxWidth: '1504px',
  width: '100%',
  marginInline: 'auto',
})

export const titleClass = css({
  textStyle: { base: 'title-14', lg: 'title-16' },
})

export const carouselColumnClass = css({
  width: 'full',
  containerType: 'inline-size',
  '& [data-slot="carousel-content"]': {
    gap: { base: 'space-16', lg: 'space-24' },
  },
  '& [data-slot="carousel-item"]': {
    minWidth: 0,
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: {
      base: '56vw',
      lg: `calc((100cqi - ${COLUMNS - 1} * var(--web-ui-spacing-space-24)) / ${COLUMNS})`,
    },
  },
})

export const CAROUSEL_OPTS = {
  align: 'start' as const,
  slidesToScroll: 1,
  dragFree: true,
}

export const gridClass = css({
  display: 'grid',
  gridTemplateColumns: `repeat(${COLUMNS}, 1fr)`,
  gap: { base: 'space-16', lg: 'space-24' },
})

export function HonestReviewsDs({
  sectionConfig: { id, richTexts = [], genericContents: reviews = [], trackingMetaData, tracker, sectionType },
}: SectionProps) {
  const headingRichTextProps = RichTextDomain.makeRichTextProps(richTexts[0])
  const products = reviews.slice(0, COLUMNS)

  const getIsVisibleTrackParams = useCallback(() => ({ ...trackingMetaData }), [trackingMetaData])
  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  const productViewTracker = tracker ? `${tracker}_honest_reviews` : undefined
  useBatchedAnalyticsForProductCard({ productViewTracker, trackingMetaData })

  const contentContainerTrackingMetadata = getContentContainerTrackingMetadata(trackingMetaData)

  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP

  if (!products.length) return null

  const buildReviewCardProps = (review: GenericContent, idx: number) => {
    const product = review.products[0]
    if (!product) return null

    const trackingContext = getTrackingContext(tracker, product.slug)
    const productCard = { product }
    const { reviews: { averageRating = 0 } = {} } = product

    const wishlistTrackConfig = {
      ...trackingContext,
      sectionType,
      sectionid: id,
      ...trackingMetaData?.context,
      productPosition: idx,
      ...contentContainerTrackingMetadata,
      findingMethod: getFindingMethodAggregate(),
    }

    return {
      key: product.productId,
      productCard,
      rating: averageRating,
      // Route through `RichTextDomain.makeRichTextProps` (the canonical
      // accessor used for the heading at line 68 above). Reading `.raw`
      // directly is dangerous: in some SSR-data shapes the Contentful raw
      // field can arrive as a JSON string rather than a parsed `Document`,
      // and `RichTextDS` would silently render nothing. The domain accessor
      // is the single chokepoint where parsing/typing live.
      descriptionContent: RichTextDomain.makeRichTextProps(review.titleText)?.content,
      authorContent: RichTextDomain.makeRichTextProps(review.content)?.content,
      tracker: tracker && `${tracker}__honest_reviews`,
      productClickTrackConfig: {
        trackInternal: true,
        trackGA: true,
        ...trackingContext,
        context: {
          sectionType,
          sectionid: id,
          ...trackingMetaData?.context,
          productPosition: idx,
          ...contentContainerTrackingMetadata,
          findingMethod: getFindingMethodAggregate(),
        },
      },
      wishlistTrackConfig,
    }
  }

  const cardPropsList = products.flatMap((review, idx) => {
    const props = buildReviewCardProps(review, idx)
    return props ? [props] : []
  })

  return (
    <div ref={trackIsVisibleRef} id={id.toLowerCase()} className={containerClass} data-testid="honest-reviews-ds">
      <div className={innerClass}>
        {headingRichTextProps?.content && (
          <div data-testid={TEST_LOCATORS.HONEST_REVIEWS.HEADER(plainTextFromDocument(headingRichTextProps.content))}>
            <RichTextDS content={headingRichTextProps.content} className={titleClass} />
          </div>
        )}
        {isDesktop ? (
          <div className={gridClass}>
            {cardPropsList.map(({ key, ...cardProps }, index) => (
              <div key={key} data-testid={TEST_LOCATORS.HONEST_REVIEWS.PRODUCT(index)}>
                <ProductReviewCardDs {...cardProps} />
              </div>
            ))}
          </div>
        ) : (
          <Carousel opts={CAROUSEL_OPTS}>
            <div className={carouselColumnClass}>
              <CarouselContent>
                {cardPropsList.map(({ key, ...cardProps }, index) => (
                  <CarouselItem key={key} data-testid={TEST_LOCATORS.HONEST_REVIEWS.PRODUCT(index)}>
                    <ProductReviewCardDs {...cardProps} />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </div>
          </Carousel>
        )}
      </div>
    </div>
  )
}

export default HonestReviewsDs
